// Stories Tab Styles
// Grid layout for profile icons with stories

import styled, { css } from "styled-components";
import { theme } from "../theme";

// Main Container
export const StoriesGridContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  background: ${theme.colors.background.primary};
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 100dvh;
  }
`;

// Grid Container - Uniform grid layout
export const StoriesGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
  padding: ${theme.spacing.md};
  padding-top: calc(${theme.spacing.md} + 60px); /* Account for top navigation */
  padding-bottom: calc(${theme.spacing.md} + 70px); /* Account for bottom navigation */

  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  /* Uniform grid layout */
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.md} ${theme.spacing.sm}; /* row-gap column-gap - smaller row gap */
  justify-items: center;
  align-items: start;

  @media (min-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${theme.spacing.md} ${theme.spacing.lg}; /* row-gap column-gap - smaller row gap */
    padding: ${theme.spacing.lg};
    padding-top: calc(${theme.spacing.lg} + 60px);
    padding-bottom: calc(${theme.spacing.lg} + 70px);
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: ${theme.spacing.md} ${theme.spacing.xl}; /* row-gap column-gap - smaller row gap */
    padding: ${theme.spacing.xl};
    padding-top: calc(${theme.spacing.xl} + 60px);
    padding-bottom: calc(${theme.spacing.xl} + 70px);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }

  /* Smooth scrolling */
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
`;

// Profile Icon with Glowing Ring for Stories
export const StoryProfileIcon = styled.div<{ $hasStories: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: ${theme.borderRadius.full};
  overflow: visible;
  cursor: pointer;
  border: 3px solid ${theme.colors.primary};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  ${props => props.$hasStories ? css`
    &::before {
      content: '';
      position: absolute;
      top: -5px;
      left: -5px;
      right: -5px;
      bottom: -5px;
      border-radius: ${theme.borderRadius.full};
      background: linear-gradient(45deg, #ff6b9d, #c06c84, #ffd700, #ffa500, #ff6b9d);
      background-size: 400% 400%;
      z-index: 0;
      animation: storyRing 3s ease infinite;
      pointer-events: none;
    }
    
    @keyframes storyRing {
      0% {
        background-position: 0% 50%;
        opacity: 1;
      }
      50% {
        background-position: 100% 50%;
        opacity: 0.8;
      }
      100% {
        background-position: 0% 50%;
        opacity: 1;
      }
    }
  ` : ''}

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${theme.borderRadius.full};
    position: relative;
    z-index: 1;
    display: block;
  }
`;

// Profile Icon Container - Wraps icon and name
export const StoryProfileItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xs};
  cursor: pointer;
`;

// User Name under Profile Icon
export const StoryProfileName = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${theme.colors.text.primary};
  text-align: center;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: ${theme.spacing.xs};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.7rem;
  }
`;

// Empty State
export const StoriesEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: ${theme.colors.text.secondary};
  padding: ${theme.spacing.xl};
  padding-top: calc(${theme.spacing.xl} + 60px);
  padding-bottom: calc(${theme.spacing.xl} + 70px);
`;

export const StoriesEmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${theme.spacing.md};
  opacity: 0.5;
`;

export const StoriesEmptyStateTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 ${theme.spacing.sm} 0;
  color: ${theme.colors.text.primary};
`;

export const StoriesEmptyStateDescription = styled.p`
  font-size: 1rem;
  margin: 0;
  max-width: 400px;
  line-height: 1.5;
  color: ${theme.colors.text.secondary};
`;

